import { useState, useEffect } from 'react';
import { supabase } from './supabase';
import { Card, Pill, Dot, Mono } from './components/ui';

// Current report shape (2026-06 merge: 7 sections consolidated into 4).
// Legacy keys stay below so reports generated before the merge still render —
// the filter on report.sections picks whichever set the stored JSON contains.
const SECTIONS = [
  { key: 'market_pricing',        label: 'Market Pricing',     icon: 'price_change',   color: 'var(--g-danger)' },
  { key: 'promotions',            label: 'Promotions',         icon: 'local_offer',    color: 'var(--g-warn)' },
  { key: 'assortment_changes',    label: 'Assortment',         icon: 'new_releases',   color: 'var(--g-accent-hi)' },
  { key: 'price_comparisons',     label: 'Price Comparisons',  icon: 'compare_arrows', color: 'var(--g-info)' },
  // Legacy (pre-merge) section keys
  { key: 'price_changes',         label: 'Price Changes',      icon: 'price_change',   color: 'var(--g-danger)' },
  { key: 'active_sales',          label: 'Active Sales',       icon: 'local_offer',    color: 'var(--g-warn)' },
  { key: 'new_products',          label: 'New Products',       icon: 'new_releases',   color: 'var(--g-accent-hi)' },
  { key: 'brand_category_shifts', label: 'Brand & Category',   icon: 'analytics',      color: '#a78bfa' },
  { key: 'market_benchmarks',     label: 'Market Benchmarks',  icon: 'show_chart',     color: 'var(--g-accent-hi)' },
  { key: 'competitor_intel',      label: 'Competitor Intel',   icon: 'radar',          color: 'var(--g-promo)' },
];

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
  });
}

function hexAlpha(hex, alpha) {
  // Token colors (var(--…)) can't be sliced into RGB — mix toward transparent.
  if (!hex || hex[0] !== '#') return `color-mix(in srgb, ${hex} ${alpha * 100}%, transparent)`;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

export default function AIReport({ myStoreId }) {
  const [report,    setReport]    = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [activeTab, setActiveTab] = useState(SECTIONS[0].key);
  const [taxStores, setTaxStores] = useState([]);

  useEffect(() => {
    if (!myStoreId) {
      setLoading(false);
      return;
    }
    async function fetchReport() {
      const { data, error } = await supabase
        .from('daily_reports')
        .select('report_date, sections, model, created_at')
        .eq('client_id', myStoreId)
        .order('report_date', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) {
        console.error('[AIReport] fetch error:', error);
        setFetchError(error.message || 'Unknown error');
      }
      setReport(data || null);
      setLoading(false);
    }
    async function fetchTaxStores() {
      const { data } = await supabase
        .from('stores')
        .select('name')
        .eq('tax_inclusive', true)
        .order('name');
      setTaxStores((data || []).map(s => s.name));
    }
    fetchReport();
    fetchTaxStores();
  }, [myStoreId]);

  if (loading) {
    return (
      <Card accent style={{ padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <Pill tone="vine"><Dot color="var(--g-accent-hi)" pulse /> AI Daily Brief</Pill>
          <Mono size={12} color="var(--g-text-faint)">loading…</Mono>
        </div>
        {[80, 60, 40].map(w => (
          <div
            key={w}
            className="animate-pulse"
            style={{
              width: `${w}%`,
              height: 12,
              background: 'rgba(148,163,184,0.12)',
              borderRadius: 4,
              marginBottom: 8,
            }}
          />
        ))}
      </Card>
    );
  }

  if (fetchError) {
    return (
      <Card style={{ padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <div style={{ width: 3, alignSelf: 'stretch', background: 'rgba(239,68,68,0.5)', borderRadius: 1.5, flexShrink: 0 }} />
          <div>
            <Pill tone="danger">AI Daily Brief · Access Error</Pill>
            <Mono size={12} color="var(--g-text-dim)" style={{ marginTop: 6, display: 'block' }}>
              {fetchError}
            </Mono>
          </div>
        </div>
      </Card>
    );
  }

  if (!report || !report.sections) {
    return (
      <Card style={{ padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <div style={{ width: 3, alignSelf: 'stretch', background: 'rgba(16,185,129,0.3)', borderRadius: 1.5, flexShrink: 0 }} />
          <div>
            <Pill tone="vine">AI Daily Brief</Pill>
            <p style={{ fontSize: 13, color: 'var(--g-text-dim)', marginTop: 8, marginBottom: 4 }}>
              Today's brief is being prepared.
            </p>
            <Mono size={12} color="var(--g-accent-hi)">Your AI competitive brief is generated each morning after the daily market scan — check back soon.</Mono>
          </div>
        </div>
      </Card>
    );
  }

  const s = report.sections;

  if (s.parse_error) {
    return (
      <Card accent style={{ padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <Pill tone="amber">AI Daily Brief · {formatDate(report.report_date)}</Pill>
        </div>
        <Mono size={12} color="var(--g-text-dim)" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
          {s.raw}
        </Mono>
      </Card>
    );
  }

  const availableSections = SECTIONS.filter(sec => s[sec.key]);
  const currentDef = availableSections.find(sec => sec.key === activeTab) || availableSections[0];
  const sec = currentDef ? s[currentDef.key] : null;

  return (
    <Card accent style={{ overflow: 'hidden' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 22px',
          borderBottom: '1px solid var(--g-border)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Pill tone="vine"><Dot color="var(--g-accent-hi)" pulse /> AI Daily Brief</Pill>
          <Mono size={12} color="var(--g-text-dim)">{formatDate(report.report_date)}</Mono>
        </div>
      </div>

      {/* Executive summary */}
      {s.executive_summary && (
        <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--g-border-faint)' }}>
          <p
            style={{
              margin: 0,
              fontSize: 14,
              fontWeight: 500,
              color: 'var(--g-text)',
              lineHeight: 1.6,
              letterSpacing: '-0.005em',
            }}
          >
            {s.executive_summary}
          </p>
        </div>
      )}

      {/* Tab bar */}
      <div
        className="hide-scrollbar"
        style={{
          display: 'flex',
          overflowX: 'auto',
          padding: '0 16px',
          gap: 4,
          borderBottom: '1px solid var(--g-border-faint)',
        }}
      >
        {availableSections.map(({ key, label, icon, color }) => {
          const active = currentDef?.key === key;
          return (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '11px 14px',
                whiteSpace: 'nowrap',
                fontFamily: 'Inter, sans-serif',
                fontSize: 12,
                fontWeight: active ? 600 : 500,
                color: active ? color : 'var(--g-text-dim)',
                borderBottom: active ? `2px solid ${color}` : '2px solid transparent',
                marginBottom: -1,
                background: 'transparent',
                border: 'none',
                borderBottomWidth: 2,
                cursor: 'pointer',
                flexShrink: 0,
              }}
              onMouseEnter={e => { if (!active) e.currentTarget.style.color = 'var(--g-text)'; }}
              onMouseLeave={e => { if (!active) e.currentTarget.style.color = 'var(--g-text-dim)'; }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 14 }}>{icon}</span>
              {label}
            </button>
          );
        })}
      </div>

      {/* Active tab content */}
      {sec && currentDef && (
        <div style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {sec.headline && (
            <p
              style={{
                margin: 0,
                fontSize: 16,
                fontWeight: 700,
                color: currentDef.color,
                lineHeight: 1.5,
                letterSpacing: '-0.01em',
              }}
            >
              {sec.headline}
            </p>
          )}

          {sec.bullets?.length > 0 && (
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {sec.bullets.map((b, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      background: currentDef.color,
                      flexShrink: 0,
                      marginTop: 7,
                    }}
                  />
                  <span style={{ fontSize: 13, color: 'var(--g-text)', lineHeight: 1.55 }}>{b}</span>
                </li>
              ))}
            </ul>
          )}

          {sec.strategy_insight && (
            <div
              style={{
                padding: '12px 14px',
                borderRadius: 6,
                background: hexAlpha(currentDef.color, 0.06),
                border: `1px solid ${hexAlpha(currentDef.color, 0.25)}`,
              }}
            >
              <Mono
                size={12}
                color={currentDef.color}
                style={{ letterSpacing: '0.01em', display: 'block', marginBottom: 6, fontWeight: 600 }}
              >
                Strategy Signal
              </Mono>
              <p style={{ margin: 0, fontSize: 13, color: 'var(--g-text-dim)', lineHeight: 1.55 }}>
                {sec.strategy_insight}
              </p>
            </div>
          )}

          {currentDef.key === 'price_comparisons' && taxStores.length > 0 && (
            <div
              style={{
                padding: '12px 14px',
                borderRadius: 6,
                background: 'rgba(96,165,250,0.06)',
                border: '1px solid rgba(96,165,250,0.25)',
              }}
            >
              <Mono
                size={12}
                color="var(--g-info)"
                style={{ letterSpacing: '0.01em', display: 'block', marginBottom: 8, fontWeight: 600 }}
              >
                Tax-inclusive pricing — normalized for fair comparison
              </Mono>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {taxStores.map(store => (
                  <Pill key={store} tone="info">{store}</Pill>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Recommendation */}
      {s.recommendation && (
        <div
          style={{
            padding: '16px 22px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 14,
            borderTop: '1px solid var(--g-border)',
            background: 'rgba(16,185,129,0.05)',
          }}
        >
          <div style={{ width: 3, alignSelf: 'stretch', background: 'var(--g-accent)', borderRadius: 1.5, flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <Mono
              size={12}
              color="var(--g-accent-hi)"
              style={{ letterSpacing: '0.01em', display: 'block', marginBottom: 4, fontWeight: 600 }}
            >
              This week&apos;s action
            </Mono>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--g-text)', lineHeight: 1.6 }}>
              {s.recommendation}
            </p>
          </div>
        </div>
      )}
    </Card>
  );
}
